import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Flame,
  AlertTriangle,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Lock,
  RefreshCw,
  Copy,
} from "lucide-react";
import { bondsAPI, formatCurrency, formatDate } from "../../services/api";
import burnRequestsAPI from "../../services/burnRequestsAPI";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { toast } from "sonner";
import { promptToast } from "../../utils/sonnerDialogs";

const TokenBurn = () => {
  const navigate = useNavigate();
  const { getDisplayName } = useAuth();
  const queryClient = useQueryClient();

  const [selectedToken, setSelectedToken] = useState(null);
  const [showBurnModal, setShowBurnModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedBurnRequest, setSelectedBurnRequest] = useState(null);
  const [showBurnRequestModal, setShowBurnRequestModal] = useState(false);

  // Fetch burn requests from API
  const {
    data: burnRequestsData,
    isLoading: burnRequestsLoading,
    error: burnRequestsError,
    refetch: refetchBurnRequests,
  } = useQuery({
    queryKey: ["burn-requests"],
    queryFn: () =>
      burnRequestsAPI.getAllBurnRequests({
        page: 1,
        limit: 50,
        sortBy: "requestDate",
        sortOrder: "desc",
      }),
    refetchInterval: 30000, // Refetch every 30 seconds
    select: (data) => {
      // Filter and process burn requests data without logging raw API response
      return data.data?.data || data.data || [];
    },
  });

  const burnRequests = burnRequestsData || [];

  // Approve burn request mutation
  const approveBurnRequestMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return burnRequestsAPI.approveBurnRequest(id, data);
    },
    onSuccess: (response) => {
      toast.success("Burn request approved successfully!");
      queryClient.invalidateQueries({ queryKey: ["burn-requests"] });
      setShowBurnRequestModal(false);
      setSelectedBurnRequest(null);
    },
    onError: (error) => {
      console.error("Approve burn request failed:", error);
      toast.error(
        `Failed to approve burn request: ${
          error.response?.data?.message || error.message
        }`
      );
    },
  });

  // Reject burn request mutation
  const rejectBurnRequestMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return burnRequestsAPI.rejectBurnRequest(id, data);
    },
    onSuccess: (response) => {
      toast.success("Burn request rejected successfully!");
      queryClient.invalidateQueries({ queryKey: ["burn-requests"] });
      setShowBurnRequestModal(false);
      setSelectedBurnRequest(null);
    },
    onError: (error) => {
      console.error("Reject burn request failed:", error);
      toast.error(
        `Failed to reject burn request: ${
          error.response?.data?.message || error.message
        }`
      );
    },
  });

  // Handle approve burn request
  const handleApproveBurnRequest = (burnRequest) => {
    setSelectedBurnRequest(burnRequest);
    setShowBurnRequestModal(true);
  };

  // Handle reject burn request
  const handleRejectBurnRequest = async (burnRequest) => {
    const rejectionReason = await promptToast(
      "Please provide a reason for rejection:",
      {
        placeholder: "Enter rejection reason",
        confirmLabel: "Reject request",
      }
    );
    if (rejectionReason) {
      rejectBurnRequestMutation.mutate({
        id: burnRequest._id,
        data: { rejectionReason },
      });
    }
  };

  // Copy transaction hash to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy to clipboard");
      });
  };

  // Helper function to determine burn status based on bond data
  const getBurnStatus = (bond) => {
    const today = new Date();
    const maturityDate = new Date(bond.maturityDate);

    if (bond.status === "Burned") {
      return "Fully Burned";
    } else if (bond.status === "Approved" && maturityDate < today) {
      return "Ready for Burn";
    } else if (bond.status === "Approved") {
      return "Not Yet Matured";
    } else {
      return "Not Available";
    }
  };

  // Fetch bonds from API
  const {
    data: bondsData,
    isPending: bondsLoading,
    error: bondsError,
  } = useQuery({
    queryKey: ["bonds", "token-burn"],
    queryFn: () =>
      bondsAPI.getBonds({
        page: 1,
        limit: 50,
      }),
    select: (data) => {
      const bonds = data.data.bonds || [];
      // Transform API bond data to match the expected format for token burning
      return bonds.map((bond) => ({
        id: bond._id,
        bondName: bond.bondName,
        bondId: bond.bondNumber || bond._id,
        tokenSymbol:
          bond.tokenSymbol ||
          `${bond.bondName?.replace(/\s+/g, "").slice(0, 6).toUpperCase()}`,
        totalSupply: bond.issueAmount || 0,
        circulatingSupply: bond.issueAmount || 0, // In real scenario, this would come from blockchain
        redeemedAmount: 0, // This would be calculated from redemption data
        burnableAmount: 0, // This would be calculated based on redeemed amount
        maturityDate: bond.maturityDate,
        redemptionDate: null, // This would come from redemption records
        status: getBurnStatus(bond),
        contractAddress:
          bond.tokenContractAddress ||
          "0x0000000000000000000000000000000000000000",
        burnHistory: [], // This would come from burn transaction records
        // Additional bond data
        isin: bond.isin,
        issuer: bond.issuer,
        productType: bond.productType,
        couponRate: bond.couponRate,
        profitRate: bond.profitRate,
      }));
    },
  });

  const burnableTokens = bondsData || [];

  // Token burn mutation
  const burnTokenMutation = useMutation({
    mutationFn: async ({ bondId, burnData }) => {
      // Update bond status to 'Burned' in the database
      return bondsAPI.updateBond(bondId, {
        status: "Burned",
        burnedAt: new Date().toISOString(),
        burnedBy: getDisplayName(),
        burnAmount: burnData.amount,
        burnReason: burnData.reason,
        confirmationCode: burnData.confirmationCode,
      });
    },
    onSuccess: (response, variables) => {
      // Invalidate and refetch bonds data
      queryClient.invalidateQueries({ queryKey: ["bonds", "token-burn"] });

      // Show success message
      toast.success(`Token burn successfully!`);

      // Close modal and reset state
      setShowBurnModal(false);
      setSelectedToken(null);
    },
    onError: (error) => {
      console.error("Token burn failed:", error);
      toast.error(`token burn failed: ${error.message || "burn failed"}`);
    },
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      Pending: { color: "yellow", icon: Clock },
      Approved: { color: "green", icon: CheckCircle },
      Rejected: { color: "red", icon: XCircle },
      "Ready for Burn": { color: "red", icon: Flame },
      "Not Yet Matured": { color: "yellow", icon: Clock },
      "Not Available": { color: "gray", icon: Lock },
      "Partial Burn Available": { color: "blue", icon: Clock },
      "Fully Burned": { color: "gray", icon: CheckCircle },
      "Burn in Progress": { color: "orange", icon: Flame },
      "Burn Requested": { color: "purple", icon: AlertTriangle },
      "Burned Successfully": { color: "green", icon: CheckCircle },
      Failed: { color: "red", icon: XCircle },
    };
    const config = statusConfig[status] || { color: "gray", icon: FileText };
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  // Handle loading state
  if (burnRequestsLoading) {
    return <LoadingSpinner size="lg" text="Loading burn requests..." />;
  }

  // Handle error state
  if (burnRequestsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Error Loading Burn Requests
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {burnRequestsError.message || "Failed to load burn request data"}
          </p>
          <button
            onClick={() => refetchBurnRequests()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Token Burn</h1>
              <p className="text-sm text-gray-600">
                Permanently remove tokens from circulation
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Warning Banner */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">
                Critical Operation Warning
              </h3>
              <p className="text-sm text-red-700 mt-1">
                Token burning is irreversible. Burned tokens are permanently
                removed from circulation and cannot be recovered. Only proceed
                if you are certain about this action.
              </p>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Burn Requests
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {burnRequests.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Pending Approval
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {
                        burnRequests.filter((req) => req.status === "Pending")
                          .length
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Approved
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {
                        burnRequests.filter((req) => req.status === "Approved")
                          .length
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Rejected
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {
                        burnRequests.filter((req) => req.status === "Rejected")
                          .length
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Burn Requests Section */}
        {burnRequests.length > 0 && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Burn Requests
                  </h3>
                  <p className="text-sm text-gray-600">
                    Review and manage burn requests from Facility Agent
                  </p>
                </div>
                <button
                  onClick={() => refetchBurnRequests()}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bond Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Token Information
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Request Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {burnRequests.map((request) => (
                    <tr key={request._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {request.bondId?.bondName || "N/A"}
                          </div>
                          <div className="text-xs text-gray-400">
                            ISIN: {request.bondId?.isin || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div>
                            Amount:{" "}
                            {formatCurrency(request.totalTokensToBurn || 0)}
                          </div>
                          <div>
                            Bond Type: {request.bondId?.productType === "Fixed Notes" ? "Conventional" : (request.bondId?.productType || "N/A")}
                          </div>
                          <div className="text-xs text-gray-500">
                            Maturity: {formatDate(request.bondId?.maturityDate)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div>
                            By:{" "}
                            {typeof request.requestedBy === "object" &&
                            request.requestedBy
                              ? request.requestedBy.name ||
                                request.requestedBy.username ||
                                "N/A"
                              : request.requestedBy || "N/A"}
                          </div>
                          <div>Date: {formatDate(request.requestDate)}</div>
                          <div className="text-xs text-gray-500">
                            Reason: {request.burnReason || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(request.status)}
                        {request.transactionHash && (
                          <div className="text-xs text-green-600 mt-1 flex items-center">
                            <span>
                              TX: {request.transactionHash.slice(0, 8)}...
                            </span>
                            <button
                              onClick={() =>
                                copyToClipboard(request.transactionHash)
                              }
                              className="ml-1 text-gray-400 hover:text-gray-600"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        {request.rejectionReason && (
                          <div className="text-xs text-red-600 mt-1">
                            Reason: {request.rejectionReason}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {request.status === "Pending" && (
                            <>
                              <button
                                onClick={() =>
                                  handleApproveBurnRequest(request)
                                }
                                disabled={approveBurnRequestMutation.isPending}
                                className="text-green-600 hover:text-green-900 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectBurnRequest(request)}
                                disabled={rejectBurnRequestMutation.isPending}
                                className="text-red-600 hover:text-red-900 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </button>
                            </>
                          )}
                          {request.status === "Approved" &&
                            request.transactionHash && (
                              <a
                                href={`https://teleskop.aeris-dev.codedsolution-web3.com/tx/${request.transactionHash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-900 flex items-center"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View TX
                              </a>
                            )}
                          {request.status === "Rejected" && (
                            <span className="text-gray-400 text-sm">
                              No actions available
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Burn Request Approval Modal */}
      {showBurnRequestModal && selectedBurnRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Approve Burn Request
              </h3>
            </div>

            <div className="mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-green-900">
                  {selectedBurnRequest.bondId?.bondName || "N/A"}
                </h4>
                <p className="text-sm text-green-700 mt-1">
                  ISIN: {selectedBurnRequest.bondId?.isin || "N/A"} • Requested
                  by:{" "}
                  {typeof selectedBurnRequest.requestedBy === "object" &&
                  selectedBurnRequest.requestedBy
                    ? selectedBurnRequest.requestedBy.name ||
                      selectedBurnRequest.requestedBy.username ||
                      "N/A"
                    : selectedBurnRequest.requestedBy || "N/A"}
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-gray-900 mb-2">
                  Burn Request Details
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Bond Amount:</span>{" "}
                    {formatCurrency(selectedBurnRequest.totalTokensToBurn || 0)}
                  </div>
                  <div>
                    <span className="text-gray-500">Request Date:</span>{" "}
                    {formatDate(selectedBurnRequest.requestDate)}
                  </div>
                  <div>
                    <span className="text-gray-500">Maturity Date:</span>{" "}
                    {formatDate(selectedBurnRequest.bondId?.maturityDate)}
                  </div>
                  <div>
                    <span className="text-gray-500">Bond Type:</span>{" "}
                    {selectedBurnRequest.bondId?.productType === "Fixed Notes" ? "Conventional" : (selectedBurnRequest.bondId?.productType || "N/A")}
                  </div>
                </div>
                <div className="mt-2 text-sm">
                  <span className="text-gray-500">Burn Reason:</span>{" "}
                  {selectedBurnRequest.burnReason || "N/A"}
                </div>
                {selectedBurnRequest.notes && (
                  <div className="mt-2 text-sm">
                    <span className="text-gray-500">Notes:</span>{" "}
                    {selectedBurnRequest.notes}
                  </div>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">
                      Approval Confirmation
                    </h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      By approving this request, you confirm that all tokens for
                      this bond should be permanently burned. This action will
                      generate a transaction hash for audit purposes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBurnRequestModal(false);
                  setSelectedBurnRequest(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  approveBurnRequestMutation.mutate({
                    id: selectedBurnRequest._id,
                    data: {
                      actualTokensBurned: selectedBurnRequest.totalTokensToBurn,
                      notes: `Burn request approved for ${
                        selectedBurnRequest.bondId?.bondName || "Bond"
                      }`,
                    },
                  });
                }}
                disabled={approveBurnRequestMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {approveBurnRequestMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve Burn Request
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenBurn;
